import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import comprasAnalisisService, { ComprasAnalisisStockDocumento } from '../../services/comprasAnalisisService';
import { getApiErrorMessage } from '../../utils/apiError';

export type StockNetoDocumentosTipo = 'reservas' | 'oc-pendientes';

interface Props {
  open: boolean;
  onClose: () => void;
  codigoArticulo: string;
  tipo: StockNetoDocumentosTipo | null;
}

const formatCantidad = (value: number): string =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(value);

const formatFecha = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR');
};

const titulos: Record<StockNetoDocumentosTipo, { title: string; entidad: string }> = {
  reservas: { title: 'Pedidos con stock reservado', entidad: 'Cliente' },
  'oc-pendientes': { title: 'Órdenes de compra pendientes de recibir', entidad: 'Proveedor' },
};

const StockNetoDocumentosDialog: React.FC<Props> = ({ open, onClose, codigoArticulo, tipo }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ComprasAnalisisStockDocumento[]>([]);
  const [total, setTotal] = useState(0);
  const [reservaActiva, setReservaActiva] = useState(true);

  useEffect(() => {
    if (!open || !tipo || !codigoArticulo) {
      setItems([]);
      setTotal(0);
      setReservaActiva(true);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetcher =
      tipo === 'reservas'
        ? comprasAnalisisService.getReservasPedidos(codigoArticulo)
        : comprasAnalisisService.getOcPendientesDetalle(codigoArticulo);

    void fetcher
      .then((resp) => {
        if (!cancelled) {
          setItems(resp.items || []);
          setTotal(resp.total || 0);
          setReservaActiva(resp.reservaActiva !== false);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, 'No se pudo cargar el detalle.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tipo, codigoArticulo]);

  if (!tipo) return null;

  const meta = titulos[tipo];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          {meta.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {codigoArticulo}
        </Typography>
        <IconButton aria-label="Cerrar" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : items.length === 0 ? (
          <Alert severity="info">No hay documentos pendientes para este artículo.</Alert>
        ) : (
          <>
          {tipo === 'reservas' && !reservaActiva ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              El tipo de formulario Pedido no tiene Descuenta stock activo: estos pedidos no comprometen
              el disponible ni el reservado.
            </Alert>
          ) : null}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Comprobante</TableCell>
                  <TableCell>{meta.entidad}</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={`${row.nroFormulario}-${row.nroItem}`}>
                    <TableCell>{formatFecha(row.fechaEmision)}</TableCell>
                    <TableCell>{row.comprobante}</TableCell>
                    <TableCell>
                      {row.entidadNumero ? `${row.entidadNumero} - ` : ''}
                      {row.entidadNombre || '—'}
                    </TableCell>
                    <TableCell align="right">{formatCantidad(row.cantidad)}</TableCell>
                  </TableRow>
                ))}
                <TableRow selected>
                  <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatCantidad(total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockNetoDocumentosDialog;
